// CeKKeU — SINGLE SOURCE OF TRUTH for cross-screen financial classification.
// PURE, CLIENT-SIDE ONLY. No deps, so any engine (dailyHealth.js, insights.js)
// and any component can import it without circular imports.
//
// Two things live here so different screens can NEVER drift apart:
//   1. Score classification bands (0 = worst, 100 = best — higher always better).
//   2. Emergency-fund runway (one denominator definition for all screens).

// ---------- Score classification (consistent, higher = better) ----------
// Kept aligned with the validated daily-health methodology (kritis <50,
// waspada 50–71, sehat >=72). We DO NOT change the score→band mapping here,
// we only expose labels + human-readable ranges so the UI is explainable.
export const SCORE_BANDS = [
  { key: "kritis", label: "Kritis", color: "rose", min: 0, max: 49 },
  { key: "waspada", label: "Waspada", color: "amber", min: 50, max: 71 },
  { key: "sehat", label: "Sehat", color: "emerald", min: 72, max: 100 },
];

export const SCORE_LEGEND = "0–49 Kritis · 50–71 Waspada · 72–100 Sehat";

export function classifyScore(value) {
  const v = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
  const band = SCORE_BANDS.find((b) => v >= b.min && v <= b.max) || SCORE_BANDS[0];
  return { value: v, ...band, rangeLabel: `${band.min}–${band.max}` };
}

// ---------- Essential monthly obligations (the emergency-runway denominator) ----------
// Definition: fixed monthly commitments the family must keep paying — configured
// recurring expenses + configured debt payments. This is the "wajib" basis.
export function essentialMonthly(finance) {
  const recurring = (finance?.recurringExpenses || []).filter((s) => s.active !== false);
  const debts = (finance?.debts || []).filter((s) => s.active !== false);
  const recurringSum = recurring.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const debtSum = debts.reduce((s, x) => s + (Number(x.monthly_payment) || 0), 0);
  return recurringSum + debtSum;
}

// Average REGULAR monthly spend from recorded transactions (fallback denominator
// when no fixed obligations are configured). Excludes one-off outliers (>4x median)
// so a single large purchase does not distort the basis.
export function avgRegularMonthlySpend(finance, now = new Date()) {
  const tx = (finance?.transactions || []).filter((t) => t.type === "expense");
  if (!tx.length) return 0;
  const amts = tx.map((t) => Number(t.amount) || 0).sort((a, b) => a - b);
  const median = amts[Math.floor(amts.length / 2)] || 0;
  const th = median > 0 ? median * 4 : Infinity;
  const regular = tx.filter((t) => (Number(t.amount) || 0) <= th);
  const dates = tx.map((t) => new Date(t.date));
  const first = new Date(Math.min(...dates));
  const nowD = now instanceof Date ? now : new Date(now);
  const months = Math.max(1, (nowD.getFullYear() - first.getFullYear()) * 12 + (nowD.getMonth() - first.getMonth()) + 1);
  const total = regular.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  return total / months;
}

// SINGLE emergency-fund runway used by every tracking screen (Analisis, Beranda,
// Ringkasan Bulanan, PDF). Runway = Emergency Fund ÷ Essential Monthly Expenses.
// Never fabricates precision: if no denominator basis exists, returns available:false.
export function emergencyRunway(finance, now = new Date()) {
  const amount = Number(finance?.emergencyFund?.current_amount) || 0;
  const essential = essentialMonthly(finance);
  let denom = essential;
  let basis = "wajib"; // fixed obligations (recurring + cicilan)
  if (denom <= 0) {
    denom = avgRegularMonthlySpend(finance, now);
    basis = denom > 0 ? "estimasi" : null; // estimated from recorded spending
  }
  if (amount <= 0) {
    // No emergency-fund data entered → do not invent a runway.
    return { amount: 0, months: null, denom, basis, available: false, hasFund: false };
  }
  if (!denom || denom <= 0) {
    // Have a fund but no spending basis yet → cannot compute months honestly.
    return { amount, months: null, denom: 0, basis: null, available: false, hasFund: true };
  }
  return { amount, months: amount / denom, denom, basis, available: true, hasFund: true };
}

export function emergencyBasisLabel(basis) {
  if (basis === "wajib") return "pengeluaran wajib bulanan (cicilan + kebutuhan rutin)";
  if (basis === "estimasi") return "rata-rata pengeluaran rutin Anda (estimasi)";
  return "";
}
